import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

export default function DepartmentsView() {
  const [depts, setDepts] = useState([]);
  const [stats, setDeptStats] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', categories: '', service_types: '', auto_assign: true, sla_minutes: '' });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [deptRes, statsRes, techRes] = await Promise.all([
          api.get('/api/departments'),
          api.get('/api/departments/routing-stats'),
          api.get('/api/technicians'),
        ]);
        setDepts(deptRes.data.departments || []);
        setDeptStats(statsRes.data.stats || []);
        setTechs(techRes.data.technicians || []);
        if ((deptRes.data.departments || []).length === 0) {
          await api.post('/api/departments/seed');
          const { data } = await api.get('/api/departments');
          setDepts(data.departments || []);
        }
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    const payload = {
      name: form.name,
      description: form.description,
      categories: form.categories.split(',').map(s => s.trim()).filter(Boolean),
      service_types: form.service_types.split(',').map(s => s.trim()).filter(Boolean),
      auto_assign: form.auto_assign,
      sla_override: form.sla_minutes ? { resolution_minutes: parseInt(form.sla_minutes) } : null,
    };
    try {
      if (editId) {
        const { data } = await api.put(`/api/departments/${editId}`, payload);
        setDepts(prev => prev.map(d => d.id === editId ? data : d));
        toast.success('Department updated');
      } else {
        const { data } = await api.post('/api/departments', payload);
        setDepts(prev => [...prev, data]);
        toast.success('Department created');
      }
      setForm({ name: '', description: '', categories: '', service_types: '', auto_assign: true, sla_minutes: '' });
      setShowCreate(false);
      setEditId(null);
    } catch { toast.error('Save failed'); }
  };

  const handleEdit = (dept) => {
    setForm({
      name: dept.name,
      description: dept.description || '',
      categories: (dept.categories || []).join(', '),
      service_types: (dept.service_types || []).join(', '),
      auto_assign: dept.auto_assign !== false,
      sla_minutes: dept.sla_override?.resolution_minutes || '',
    });
    setEditId(dept.id);
    setShowCreate(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/departments/${id}`);
      setDepts(prev => prev.filter(d => d.id !== id));
      toast.success('Department deleted');
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="departments-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Departments</h1>
          <p className="text-sm text-gray-500 mt-1">Manage ticket routing and team assignments</p>
        </div>
        <Button onClick={() => { setShowCreate(!showCreate); setEditId(null); setForm({ name: '', description: '', categories: '', service_types: '', auto_assign: true, sla_minutes: '' }); }} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="dept-new-btn">
          <Building2 className="w-4 h-4 mr-1.5" /> {showCreate ? 'Cancel' : 'New Department'}
        </Button>
      </div>

      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4 animate-fade-in" data-testid="dept-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="text-xs font-medium text-gray-700">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-lg" data-testid="dept-name-input" /></div>
            <div><label className="text-xs font-medium text-gray-700">Description</label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 rounded-lg" data-testid="dept-desc-input" /></div>
            <div><label className="text-xs font-medium text-gray-700">Categories (comma-sep)</label><Input value={form.categories} onChange={e => setForm({...form, categories: e.target.value})} className="mt-1 rounded-lg" placeholder="it_support, hardware" data-testid="dept-cats-input" /></div>
            <div><label className="text-xs font-medium text-gray-700">Service Types (comma-sep)</label><Input value={form.service_types} onChange={e => setForm({...form, service_types: e.target.value})} className="mt-1 rounded-lg" placeholder="remote, onsite" data-testid="dept-types-input" /></div>
            <div><label className="text-xs font-medium text-gray-700">SLA Override (minutes)</label><Input type="number" value={form.sla_minutes} onChange={e => setForm({...form, sla_minutes: e.target.value})} className="mt-1 rounded-lg" placeholder="Leave blank for default" data-testid="dept-sla-input" /></div>
            <div className="flex items-center gap-2 pt-5">
              <Switch checked={form.auto_assign} onCheckedChange={v => setForm({...form, auto_assign: v})} data-testid="dept-auto-assign" />
              <span className="text-sm text-gray-700">Auto-assign tickets</span>
            </div>
          </div>
          <Button onClick={handleSave} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="dept-save-btn">{editId ? 'Update' : 'Create'} Department</Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {depts.map(dept => {
          const stat = stats.find(s => s.department_id === dept.id) || {};
          return (
            <div key={dept.id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`dept-${dept.id}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-900">{dept.name}</h3>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => handleEdit(dept)} data-testid={`dept-edit-${dept.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-xs h-7 text-red-500" onClick={() => handleDelete(dept.id)} data-testid={`dept-del-${dept.id}`}>Delete</Button>
                </div>
              </div>
              {dept.description && <p className="text-xs text-gray-500 mb-3">{dept.description}</p>}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-gray-50 rounded p-2 text-center">
                  <p className="text-lg font-bold text-gray-900">{stat.total_tickets || 0}</p>
                  <p className="text-[10px] text-gray-500">Total</p>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <p className="text-lg font-bold text-amber-600">{stat.open_tickets || 0}</p>
                  <p className="text-[10px] text-gray-500">Open</p>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <p className="text-lg font-bold text-[#D4A843]">{dept.members?.length || 0}</p>
                  <p className="text-[10px] text-gray-500">Members</p>
                </div>
              </div>
              {dept.categories?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">{dept.categories.map((c, i) => (
                  <span key={`item-${i}`} className="text-[10px] bg-[#D4A843]/10 text-[#D4A843] px-1.5 py-0.5 rounded">{c}</span>
                ))}</div>
              )}
              <div className="flex items-center gap-3 text-[10px] text-gray-400">
                {dept.auto_assign && <span className="text-emerald-600 font-medium">Auto-assign ON</span>}
                {dept.sla_override && <span>SLA: {dept.sla_override.resolution_minutes}m</span>}
              </div>
            </div>
          );
        })}
      </div>
      {depts.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No departments configured</p>}
    </div>
  );
}
